import React, { useEffect, useState } from 'react'
import { pickCsvFile } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { CompanyPicker, NeedCompany, costGet, useCompany, useMeta, useToast } from './CostCommon.jsx'

// 원가관리 · 기준정보(품목). 삭제 대신 사용중지 — 지난 생산실적·수불 내역이 품목을 참조한다.
// 링크맘은 구코드(SI)가 고유키고 굿엠디코드가 중복이라, 두 번째 코드를 따로 들고 있어야
// 원본 시트와 대조가 된다.

const EMPTY = {
  sku: '', alt_sku: '', barcode: '', name: '', option_name: '',
  category: '', supplier: '', item_type: '제품', license_type: '', sale_price: '',
}

export default function CostItems({ currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [company, setCompany] = useCompany()
  const [meta] = useMeta()
  const [list, setList] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [filter, setFilter] = useState('')
  const [err, setErr] = useState('')
  const [notify, toast] = useToast()

  const load = () => {
    setErr('')
    costGet('/items', { company }).then(setList).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [company])

  const add = async () => {
    try {
      await portal.post('/api/cost/items/bulk', [{ ...form, company }])
      setForm(EMPTY)
      notify('품목 등록 완료')
      load()
    } catch (e) { setErr(e.message) }
  }

  const importCsv = () => pickCsvFile(async (rows) => {
    try {
      const res = await portal.post('/api/cost/items/bulk', rows.map((r) => ({ company, ...r })))
      notify(`${res.upserted}건 등록/갱신 완료${res.missing.length ? `, 사업부 미지정 ${res.missing.length}건 제외` : ''}`)
      load()
    } catch (e) { setErr(e.message) }
  })

  const deactivate = async (id) => {
    try { await portal.del(`/api/cost/items/${id}`); load() } catch (e) { setErr(e.message) }
  }

  const reactivate = async (item) => {
    try { await portal.post(`/api/cost/items/${item.id}`, { ...item, active: true }); load() }
    catch (e) { setErr(e.message) }
  }

  if (!list) return err ? <div className="error-box">{err}</div> : <div className="muted">불러오는 중…</div>

  const q = filter.trim().toLowerCase()
  const shown = list.filter((i) =>
    !q || [i.sku, i.alt_sku, i.barcode, i.name, i.option_name, i.category, i.supplier]
      .some((v) => (v || '').toLowerCase().includes(q)))

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>기준정보 · 품목 ({list.filter((i) => i.active).length}개 사용)</h2>
        <NoteDrawer target="items" screenKey="cost_items"
          context={null} contextLabel={null} onNavigate={() => {}}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <div className="row" style={{ marginBottom: 12 }}>
        <CompanyPicker value={company} onChange={setCompany} meta={meta} />
      </div>
      {!company && <NeedCompany>같은 품번이라도 사업부가 다르면 다른 품목입니다. 등록·업로드는 사업부를 골라야 합니다.</NeedCompany>}

      <div className="panel">
        <h3>신규 등록</h3>
        <div className="row">
          <input placeholder="품번(sku)*" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          <input placeholder="두 번째 코드" value={form.alt_sku} onChange={(e) => setForm({ ...form, alt_sku: e.target.value })} />
          <input placeholder="바코드" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
          <input placeholder="상품명*" style={{ width: 200 }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input placeholder="옵션" value={form.option_name} onChange={(e) => setForm({ ...form, option_name: e.target.value })} />
          <input placeholder="대분류" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <input placeholder="공급처" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          <select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })}>
            {(meta?.itemTypes || ['제품']).map((v) => <option key={v}>{v}</option>)}
          </select>
          <select value={form.license_type} onChange={(e) => setForm({ ...form, license_type: e.target.value })}>
            <option value="">라이선스 없음</option>
            <option value="디즈니">디즈니</option>
            <option value="조구만">조구만</option>
          </select>
          <input placeholder="판매가(조구만용)" style={{ width: 120 }} value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} />
          <button className="btn" onClick={add} disabled={!company || !form.sku || !form.name}>등록</button>
          <button className="btn secondary" onClick={importCsv} disabled={!company}>CSV 업로드</button>
        </div>
        <div className="hint">
          CSV 헤더: <code>sku, alt_sku, barcode, name, option_name, category, supplier, item_type, license_type, sale_price</code>.
          같은 사업부의 같은 sku는 덮어씁니다.
          라이선스가 있는 품목은 직접경비 단가를 비워두면 자동 계산됩니다 (디즈니: 개당 정액, 조구만: 판매가의 5%).
        </div>
      </div>

      <div className="panel">
        <div className="row" style={{ marginBottom: 10 }}>
          <input placeholder="검색 (품번/두번째코드/바코드/상품명/대분류/공급처)" style={{ width: 360 }}
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          <span className="muted">{shown.length}건 표시</span>
        </div>
        <table>
          <thead>
            <tr>
              {!company && <th className="l">사업부</th>}
              <th className="l">품번</th><th className="l">두번째코드</th><th className="l">상품명</th>
              <th className="l">옵션</th><th className="l">대분류</th><th className="l">공급처</th>
              <th>구분</th><th>라이선스</th><th>판매가</th><th>상태</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 500).map((i) => (
              <tr key={i.id} style={i.active ? undefined : { opacity: 0.45 }}>
                {!company && <td className="l">{i.company_name || i.company}</td>}
                <td className="l">{i.sku}</td>
                <td className="l">{i.alt_sku}</td>
                <td className="l">{i.name}</td>
                <td className="l">{i.option_name}</td>
                <td className="l">{i.category}</td>
                <td className="l">{i.supplier}</td>
                <td className="c">{i.item_type}</td>
                <td className="c">{i.license_type || '-'}</td>
                <td>{Number(i.sale_price) > 0 ? Number(i.sale_price).toLocaleString() : '-'}</td>
                <td className="c">{i.active ? '사용' : '중지'}</td>
                <td className="c">
                  {i.active
                    ? <button className="btn danger" onClick={() => deactivate(i.id)}>사용중지</button>
                    : <button className="btn secondary" onClick={() => reactivate(i)}>되살리기</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length > 500 && <div className="hint">상위 500건만 표시 중 — 검색으로 좁혀주세요.</div>}
      </div>
      {toast}
    </>
  )
}
